/**
 * homeView.ts — pure view logic for Sales Home (redesign slice 3, the
 * calm-minimal Minimal board; ADOPTION.md §15, register row "Redesign slice 3").
 * Component-free so vitest pins every rule (the dashboardView / closeFlow
 * pattern). Every figure here is DERIVED from a read path the page already
 * has — the six-month revenue series, the prior month's same-day-range figure
 * (H-1a), the deals list and the PENDING activities list that fetchSalesSummary
 * returns — and nothing is typed in or fabricated.
 * Money formatting is injected (fmt) so this module has no component import.
 */
import type { SalesDeal, SalesActivity, SalesLead } from '../../types'
import type { DealAssessment } from '../../../../api/types'
import { topOpenDeals } from './dashboardView'
import type { DeltaTone } from './trendDeltas'
import { zeroRuns } from './revenueByMonthView'
import { addWorkingDaysIso, dueDayOf, isBankHoliday, londonDay } from './workingDays'
import { dismissedRules } from './dealHealth'

const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const MONTH_SHORT_UPPER = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** "2026-08" → "August" — a fixed table over the wire's bare key; no Date
 *  parsing, no timezone (the same rule monthLabel keeps for "Aug"). */
export function monthLong(key: string): string {
  return MONTH_LONG[Number(key.slice(5, 7)) - 1] ?? key
}

/** The greeting's date line — the boards' "Saturday, 26 September": weekday,
 *  day and month in the BROWSER's local time (the visual suite pins the
 *  browser clock, so the line is deterministic there). No year: it is today. */
export function dateLine(now: Date): string {
  return `${WEEKDAY[now.getDay()]}, ${now.getDate()} ${MONTH_LONG[now.getMonth()]}`
}

// ── Won this month — the FACT delta, RULED §22.7 (2026-09-26) ──────────────
// 1. derived from wonPence, never from the rendered labels (147, not 148);
// 2. the label says the current month is PARTIAL ("September to date") —
//    in the card's FOOTNOTE row since the 2026-09-27 ruling (wonPartialLabel);
// 3. a zero prior renders ABSENT, never a percentage over zero;
// 4. no copy implies the figure is fixed — "to date", nothing final;
// 5. (H-1a, RULED 2026-10-18) LIKE FOR LIKE: the prior is the SAME day range
//    of the previous London month — the wire's wonPriorMonthToDate (1–18 Sep
//    on 18 Oct; 1–30 Sep on 31 Oct) — never the whole previous month, which
//    is what the series' second-last bucket holds. A wire without the field
//    (a backend before H-1a) renders ABSENT: the full month is not a fallback.
export interface PriorMonthToDate { from: string; through: string; wonPence: number }

export type WonDelta =
  | { kind: 'delta'; pct: number; tone: Exclude<DeltaTone, 'absent'>; prevPence: number; range: { from: string; through: string }; currentMonth: string }
  | { kind: 'absent'; reason: 'no-prior' | 'zero-prior'; range: { from: string; through: string } | null; currentMonth: string | null }

/** The series is the wire's six zero-filled buckets, ascending, last = the
 *  current month (revenueByMonthView pins that shape); `prior` is the wire's
 *  wonPriorMonthToDate, or undefined when the backend predates it. */
export function wonDelta(series: { month: string; won: number }[], prior?: PriorMonthToDate | null): WonDelta {
  const n = series.length
  const currentMonth = n ? series[n - 1].month : null
  if (!prior || !currentMonth) return { kind: 'absent', reason: 'no-prior', range: null, currentMonth }
  const range = { from: prior.from, through: prior.through }
  if (prior.wonPence === 0) return { kind: 'absent', reason: 'zero-prior', range, currentMonth }
  const pct = Math.round(((series[n - 1].won - prior.wonPence) / prior.wonPence) * 100)
  return { kind: 'delta', pct, tone: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat', prevPence: prior.wonPence, range, currentMonth }
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const dayOf = (iso: string) => Number(iso.slice(8, 10))
const monthShort = (iso: string) => MONTH_SHORT[Number(iso.slice(5, 7)) - 1] ?? iso.slice(5, 7)

/** "1–18 Sep" — the prior range as the wire's two ISO dates read; no Date
 *  parsing and no timezone (the server cut the London days, this only prints
 *  them). A one-day range prints once: "1 Sep". */
export function dayRange(range: { from: string; through: string }): string {
  const from = dayOf(range.from), through = dayOf(range.through)
  return from === through ? `${from} ${monthShort(range.through)}` : `${from}–${through} ${monthShort(range.through)}`
}

/**
 * The Won-this-month card's DELTA line: "↓35% vs £5.0M, 1–18 Sep" (RULED
 * 2026-10-18 — arrow, percentage, the prior printed on the CARD rule, the
 * prior's day range). Until H-1a it read "↑ 147% vs £2.02M in August" and
 * quoted the bar beneath it through the chart axis' formatter; the prior is
 * no longer that bar (it is part of one), so it prints as every card value
 * prints — `fmt` is fmtMoney / compactMoney. The partial-month label §22.7
 * (2) requires stays in the card's FOOTNOTE row (`wonPartialLabel`), never on
 * this line: RULED 2026-09-27 (ADOPTION.md §15.5) after the one-line sentence
 * wrapped at every width below 1702 px.
 * A zero prior says WHICH range it cannot compare against and claims nothing
 * about why: §22.7 (3) asks the copy to tell "no won deals in that range"
 * from "before this tenant existed" by the tenant's creation date, and no
 * read path carries that date (GET /v1/me: subject, provider, role,
 * tenantId, tenantName, features — measured 2026-09-27), so neither claim is
 * made until it does. No prior on the wire at all is said as such.
 */
export function wonDeltaLine(d: WonDelta, fmt: (pence: number) => string): { text: string; tone: DeltaTone } {
  if (d.kind === 'delta') {
    const arrow = d.tone === 'up' ? '↑' : d.tone === 'down' ? '↓' : '→'
    return { tone: d.tone, text: `${arrow}${Math.abs(d.pct)}% vs ${fmt(d.prevPence)}, ${dayRange(d.range)}` }
  }
  if (d.reason === 'zero-prior' && d.range) return { tone: 'absent', text: `No ${dayRange(d.range)} figure to compare against` }
  return { tone: 'absent', text: 'No prior range on the wire' }
}

/**
 * §22.7 (2): the label that says the current month is PARTIAL — "September
 * to date" — for the card's FOOTNOTE row (the `.k-metric-note` slot the other
 * three cards use), never appended to the delta line (see above). Null only
 * when the series has no current bucket. When the F-1d excluded suffix fires
 * it takes its OWN row beneath this one (SalesDashboard): two caveats, two
 * rows, a break by design at every width rather than by wrap at one.
 */
export function wonPartialLabel(d: WonDelta): string | null {
  return d.currentMonth ? `${monthLong(d.currentMonth)} to date` : null
}

// ── The greeting's insight sentence — a runtime template over two facts ──────
const WORD = ['', 'one', 'two', 'three']

export function insightSentence(input: { wonPence: number; deals: SalesDeal[]; firstStage: string }, fmt: (pence: number) => string): string {
  const first = input.wonPence > 0 ? `${fmt(input.wonPence)} won so far this month.` : 'Nothing won yet this month.'
  const top = topOpenDeals(input.deals, 3)
  if (top.length === 0) return `${first} No open deals in the pipeline.`
  const allFirst = top.every(d => d.stage === input.firstStage)
  if (allFirst) {
    return top.length === 1
      ? `${first} Your largest deal is still at ${stageDisplay(input.firstStage)}.`
      : `${first} Your ${WORD[top.length]} largest deals are still at ${stageDisplay(input.firstStage)}.`
  }
  return `${first} Your largest open deal, ${top[0].name}, is at ${stageDisplay(top[0].stage)}.`
}

// ── Largest open deal and its share of the pipeline ─────────────────────────
/** A share is one division on the GBP-comparable leg: basePence over the
 *  fenced open Σ. An uncaptured row (basePence null) has no honest share. */
export function sharePct(basePence: number | null | undefined, totalPipelinePence: number): number | null {
  if (basePence == null || !(totalPipelinePence > 0)) return null
  return Math.round((basePence / totalPipelinePence) * 100)
}

export const DEALS_LIST_CAP = 200

/** The largest OPEN deal of the list the page fetched — which is the newest
 *  200 (the D4-3 ceiling dashboardView names). Under the cap the figure is
 *  the tenant's largest; AT the cap it is the largest of the newest 200 and
 *  the note says so (the cap-sentence family). */
export function largestOpenDeal(deals: SalesDeal[], totalPipelinePence: number, cap = DEALS_LIST_CAP): { deal: SalesDeal; sharePct: number | null; capped: boolean } | null {
  const [deal] = topOpenDeals(deals, 1)
  if (!deal) return null
  return { deal, sharePct: sharePct(deal.basePence, totalPipelinePence), capped: deals.length >= cap }
}

export function largestDealNote(r: { deal: SalesDeal; sharePct: number | null; capped: boolean }): string {
  const share = r.sharePct == null ? 'share not capturable (uncaptured currency)' : `${r.sharePct}% of pipeline`
  return `${r.deal.company} · ${share}${r.capped ? ` · largest of the newest ${DEALS_LIST_CAP}` : ''}`
}

// ── Needs attention — a rule over the list: open value waiting at the first stage ──
export interface Attention { pence: number; count: number; companies: string[]; excluded: number }

export function needsAttention(deals: SalesDeal[], firstStage: string): Attention | null {
  const rows = deals.filter(d => d.stage === firstStage)
  if (rows.length === 0) return null
  const captured = rows.filter(d => d.basePence != null).sort((a, b) => (b.basePence ?? 0) - (a.basePence ?? 0))
  const companies: string[] = []
  for (const d of captured) { const c = d.company.trim(); if (c && !companies.includes(c)) companies.push(c) }
  for (const d of rows) { const c = d.company.trim(); if (c && !companies.includes(c)) companies.push(c) }
  return { pence: captured.reduce((s, d) => s + (d.basePence ?? 0), 0), count: rows.length, companies, excluded: rows.length - captured.length }
}

export function joinNames(names: string[], max = 3): string {
  const shown = names.slice(0, max)
  const rest = names.length - shown.length
  if (shown.length === 0) return ''
  if (rest > 0) return `${shown.join(', ')} and ${rest} more`
  if (shown.length === 1) return shown[0]
  return `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
}

export function attentionCopy(a: Attention, firstStage: string, fmt: (pence: number) => string): { headline: string; body: string } {
  const headline = a.pence > 0
    ? `${fmt(a.pence)} is still at ${firstStage}`
    : `${a.count} ${a.count === 1 ? 'deal is' : 'deals are'} still at ${firstStage}`
  const plural = a.companies.length > 1 || (a.companies.length === 1 && a.count > 1)
  const body = a.companies.length
    ? `${joinNames(a.companies)} ${plural ? "haven't" : "hasn't"} been qualified yet.`
    : `${a.count} ${a.count === 1 ? 'deal has' : 'deals have'} not been qualified yet.`
  return { headline, body }
}

// ── Up next — the earliest PENDING activity with a due date ─────────────────
// H-1a (RULED 2026-10-18): pending is STATUS pending — not done, not
// cancelled (a cancelled row is not up next; until H-1a it counted). The list
// Home reads is already status=pending from the server (fetchSalesSummary),
// so the 200-row cap is spent on live rows; the filter here is the same rule
// stated once more, for a caller that passes any list.
export interface UpNext {
  next: { activity: SalesActivity; overdue: boolean } | null
  pendingCount: number
  pendingWithoutDue: number
}

export const isPendingActivity = (a: SalesActivity): boolean => !a.done && a.status !== 'cancelled'

export function upNext(activities: SalesActivity[], nowMs: number): UpNext {
  const pending = activities.filter(isPendingActivity)
  const due = pending.filter(a => a.dueAt).sort((a, b) => Date.parse(a.dueAt!) - Date.parse(b.dueAt!))
  const first = due[0]
  return {
    next: first ? { activity: first, overdue: Date.parse(first.dueAt!) < nowMs } : null,
    pendingCount: pending.length,
    pendingWithoutDue: pending.length - due.length,
  }
}

export function dateBox(iso: string): { mon: string; day: string } {
  const d = new Date(iso)
  return { mon: MONTH_SHORT_UPPER[d.getMonth()], day: String(d.getDate()) }
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const dayMonth = (d: Date) => `${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`

/**
 * The Up next row's sub-line (RULED 2026-10-18), in the browser's local time
 * like the date tile beside it: "Due Mon 19 Oct" for a due date still ahead,
 * "Overdue since 12 Oct" once it has passed, and — for a row with no due
 * date, which this card never shows but a caller may pass — "Created 3 days
 * ago" (whole days on the browser clock; "today" / "yesterday" under two).
 * Until H-1a the line read "Logged N days ago" off created_at, which called a
 * SCHEDULED call logged: the data was right, the word was wrong (spec §3.3).
 */
export function upNextWhen(a: { dueAt?: string; createdAt?: string }, nowMs: number): string {
  if (a.dueAt) {
    const due = new Date(a.dueAt)
    if (due.getTime() < nowMs) return `Overdue since ${dayMonth(due)}`
    return `Due ${WEEKDAY_SHORT[due.getDay()]} ${dayMonth(due)}`
  }
  if (!a.createdAt) return 'Pending'
  const days = Math.floor((nowMs - Date.parse(a.createdAt)) / 86_400_000)
  if (!Number.isFinite(days) || days <= 0) return 'Created today'
  if (days === 1) return 'Created yesterday'
  return `Created ${days} days ago`
}

export function initials(name: string): string {
  return name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
}

// ── Home tabs H-3a (RULED 2026-10-18): the Overview's content rules ──────────
// Every rule below reads what the page already fetches — the summary, the
// deals list (the newest 200: the cap sentence rides every card computed from
// it), the PENDING activities, the quarter's /forecast and the Inbound queue.
// Thresholds are named numbers HERE and never appear in copy: the sentences
// print the real counts and the real days (R-1..R-6, 2026-10-18).
export const CONCENTRATION_PCT = 40       // a deal over this share of open value is concentrated
export const ATTENTION_ROWS = 3
export const UP_NEXT_ROWS = 3
export const REVENUE_ZERO_RUN_MONTHS = 3  // a run of zero months this long gets a note
export const REVENUE_ONE_DEAL_PCT = 80    // one deal over this share of a month's total gets a note
const DAY_MS = 86_400_000

/** Home-local sentence case for a stage label (R-1: stage labels elsewhere
 *  keep their case — that is the Calm Minimal track's): "Proposal Sent" →
 *  "Proposal sent". The DealStage value itself never changes: colours and
 *  slugs key on it. */
export function stageDisplay(stage: string): string {
  return stage ? stage.charAt(0).toUpperCase() + stage.slice(1).toLowerCase() : stage
}

/** The cap sentence (the family of "largest of the newest 200"): every card
 *  computed from the deals list says so when the list came back AT the cap —
 *  the figure is then the newest 200's, not the tenant's. Null under it. */
export function truncationNote(rows: number, cap = DEALS_LIST_CAP): string | null {
  return rows >= cap ? `From the newest ${cap} deals only.` : null
}

export const isOpenDeal = (d: SalesDeal): boolean => d.stage !== 'Closed Won' && d.stage !== 'Closed Lost'

const pad2 = (n: number) => String(n).padStart(2, '0')
const ymd = (iso: string) => iso.slice(0, 10)
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)
/** The browser's local calendar day as YYYY-MM-DD — the day the date line prints. */
export const localIsoDate = (d: Date): string => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
/** "2026-10" → "2026-10-31": the month's last day, no timezone. */
export function monthEndIso(monthKey: string): string {
  const y = Number(monthKey.slice(0, 4)), m = Number(monthKey.slice(5, 7))
  return `${monthKey}-${pad2(new Date(y, m, 0).getDate())}`
}
const dayMonthIso = (iso: string) => `${dayOf(ymd(iso))} ${monthShort(ymd(iso))}`
const checkOf = (d: SalesDeal, id: DealAssessment['checks'][number]['id']) => d.assessment?.checks.find(c => c.id === id)

// ── KPI ✦ notes ──────────────────────────────────────────────────────────────
/** Won this month's note: the open deals whose close date falls between today
 *  and the month's end (both inclusive, the server's ISO dates compared as
 *  strings), or the honest "will likely end at" when none does. */
export function wonMonthNote(deals: SalesDeal[], currentMonth: string | null, now: Date, wonPence: number, fmt: (pence: number) => string): string | null {
  if (!currentMonth) return null
  const today = localIsoDate(now), end = monthEndIso(currentMonth)
  const closing = deals.filter(d => isOpenDeal(d) && !!d.expectedClose && ymd(d.expectedClose) >= today && ymd(d.expectedClose) <= end)
  if (closing.length === 0) return `No open deals close before ${dayMonthIso(end)}, so ${monthLong(currentMonth)} will likely end at ${fmt(wonPence)}.`
  const pence = closing.reduce((s, d) => s + (d.basePence ?? 0), 0)
  return `${closing.length} ${plural(closing.length, 'deal', 'deals')} worth ${fmt(pence)} ${plural(closing.length, 'closes', 'close')} by ${dayMonthIso(end)}.`
}

/** Open pipeline's note: when the largest open deal is over CONCENTRATION_PCT
 *  of the fenced open Σ — its share and the pipeline without it. Null when it
 *  is not, or when the largest deal has no honest share. */
export function concentrationNote(deals: SalesDeal[], totalPipelinePence: number, fmt: (pence: number) => string): string | null {
  const [top] = topOpenDeals(deals, 1)
  if (!top || top.basePence == null) return null
  const share = sharePct(top.basePence, totalPipelinePence)
  if (share == null || share <= CONCENTRATION_PCT) return null
  return `${top.name} is ${share}% of it. Without ${top.name}, open pipeline is ${fmt(totalPipelinePence - top.basePence)}.`
}

/** "2026-10-01" → "Q4" — the quarter of the server's London quarterStart. */
export function quarterLabel(quarterStart: string): string {
  return `Q${Math.floor((Number(quarterStart.slice(5, 7)) - 1) / 3) + 1}`
}

/** The slice of /forecast "Likely in Q{n}" reads. */
export interface LikelyForecast {
  attributedWonPence: number
  unattributedWonPence: number
  weightedDue?: { pence: number; count: number; excludedNonGbp: number }
  totalTargetPence: number | null
  rows: { targetPence: number | null; openCount: number }[]
}
export type LikelyTarget =
  | { kind: 'none' }
  | { kind: 'partial'; targeted: number; reps: number; totalTargetPence: number }
  | { kind: 'full'; pct: number; totalTargetPence: number }

/** Likely in the quarter = the quarter's won (both attribution halves) + the
 *  weighted value of the open deals due INSIDE it (the wire's weightedDue, R-2).
 *  The target note: no target at all → none; a target, but some rep WITH open
 *  deals has none → partial coverage (no bar); every rep with open deals
 *  targeted → the attainment percentage and a bar. Null on a wire without
 *  weightedDue (a backend before H-3a): the card then says so. */
export function likelyInQuarter(f: LikelyForecast | null | undefined): { wonPence: number; weightedPence: number; pence: number; target: LikelyTarget } | null {
  if (!f || !f.weightedDue) return null
  const wonPence = f.attributedWonPence + f.unattributedWonPence
  const weightedPence = f.weightedDue.pence
  const pence = wonPence + weightedPence
  const withOpen = f.rows.filter(r => r.openCount > 0)
  const targeted = withOpen.filter(r => r.targetPence != null)
  let target: LikelyTarget
  if (f.totalTargetPence == null || f.totalTargetPence <= 0) target = { kind: 'none' }
  else if (targeted.length < withOpen.length) target = { kind: 'partial', targeted: targeted.length, reps: withOpen.length, totalTargetPence: f.totalTargetPence }
  else target = { kind: 'full', pct: Math.round((pence / f.totalTargetPence) * 100), totalTargetPence: f.totalTargetPence }
  return { wonPence, weightedPence, pence, target }
}

export function likelyTargetNote(t: LikelyTarget, quarter: string, fmt: (pence: number) => string): string {
  if (t.kind === 'none') return `No ${quarter} target yet.`
  if (t.kind === 'partial') return `${quarter} target set for ${t.targeted} of ${t.reps} ${plural(t.reps, 'rep', 'reps')} (${fmt(t.totalTargetPence)})`
  return `${t.pct}% of ${fmt(t.totalTargetPence)}`
}

/** Win rate's note from the wire's testDeals (R-3): the names, and "and n
 *  more" past the ten the wire carries. Null when no closed deal matches. */
export function testDealsNote(t?: { count: number; titles: string[] } | null): string | null {
  if (!t || t.count === 0) return null
  const rest = t.count - t.titles.length
  return `Includes test deals (${t.titles.join(', ')}${rest > 0 ? ` and ${rest} more` : ''}).`
}

// ── Revenue ✦ notes ──────────────────────────────────────────────────────────
/** (a) a run of REVENUE_ZERO_RUN_MONTHS or more months with no wins, named by
 *  its ends; (b) the most recent month whose top won deal is over
 *  REVENUE_ONE_DEAL_PCT of the month's wire total — the deal named from the
 *  list, its close month cut on London days like the series' buckets. */
export interface RevenueNote { text: string; fromList: boolean }

export function revenueNotes(series: { month: string; won: number }[], deals: SalesDeal[], fmt: (pence: number) => string): RevenueNote[] {
  const notes: RevenueNote[] = []
  for (const r of zeroRuns(series.map(m => ({ month: m.month, wonPence: m.won })))) {
    if (r.end - r.start + 1 >= REVENUE_ZERO_RUN_MONTHS) notes.push({ text: `No deals were won from ${monthLong(series[r.start].month)} to ${monthLong(series[r.end].month)}.`, fromList: false })
  }
  for (let i = series.length - 1; i >= 0; i--) {
    const m = series[i]
    if (m.won <= 0) continue
    const won = deals.filter(d => d.stage === 'Closed Won' && d.closedAt && d.basePence != null && londonDay(new Date(d.closedAt)).slice(0, 7) === m.month)
    if (won.length === 0) continue
    const top = won.reduce((a, b) => ((b.basePence ?? 0) > (a.basePence ?? 0) ? b : a))
    const pct = Math.round(((top.basePence ?? 0) / m.won) * 100)
    if (pct > REVENUE_ONE_DEAL_PCT) { notes.push({ text: `${monthLong(m.month)}'s ${fmt(m.won)} was one deal (${top.name}).`, fromList: true }); break }
  }
  return notes
}

// ── Needs attention — R1's assessment, four rules, first match wins ─────────
/** RULED 2026-10-18: a button says what the click does. Every action opens the
 *  deal (the deal page takes no field-focus target), so every label is this. */
export type AttentionAction = 'Open deal'
export interface AttentionRow { deal: SalesDeal; rule: 1 | 2 | 3 | 4; reason: string; action: AttentionAction; weight: number }
const SEVERITY: Record<DealAssessment['status'], number> = { 'at-risk': 3, 'needs-attention': 2, 'on-track': 1 }

/** RULED 2026-10-21 (Klemen, on the R3 day-1 battery): Home follows the
 *  LATEST flag action. A row rests on the ledger rule families named here
 *  (internal/salesriskflags RuleOf: close_date, stage_age, recent_activity,
 *  contact); it is dismissed when EVERY family it rests on has an open
 *  episode whose latest act is a dismiss (dealHealth.dismissedRules — the
 *  board's own reading of deal.riskFlags). A row resting on no family (no
 *  close date, a dated activity, a lead) is never dismissed: nothing in the
 *  ledger can dismiss it. A deal whose first matching rule is dismissed falls
 *  through to its next one. */
export function dismissedFor(deal: SalesDeal, families: readonly string[]): boolean {
  if (families.length === 0) return false
  const dismissed = dismissedRules(deal.riskFlags)
  return families.every(f => dismissed.has(f))
}

/** The line under a card or list that holds dismissed rows back. */
export function dismissedLine(n: number): string {
  return `${n} dismissed.`
}

/** The rules read R1 as it stands (RULED 2026-10-18): stale = the assessment's
 *  own stage.over (2× the stage's won median, else 21 days) with nothing ever
 *  logged; quiet = R1's recentActivity "quiet" (more than 14 days) on a deal
 *  over CONCENTRATION_PCT of open value; no close date; close date passed.
 *  No threshold of this file's own; the copy prints R1's day counts. Rows
 *  rank by fenced value × severity (at-risk 3, needs-attention 2, on-track 1).
 *  Dismissed rows (dismissedFor) leave rows, count and pence and are returned
 *  apart, so the card can say how many it holds back. */
export function attentionRows(deals: SalesDeal[], totalPipelinePence: number, fmt: (pence: number, currency?: string) => string, max = ATTENTION_ROWS): { rows: AttentionRow[]; count: number; pence: number; assessed: number; dismissed: AttentionRow[] } {
  void fmt
  const flagged: AttentionRow[] = []
  const held: AttentionRow[] = []
  let assessed = 0
  for (const d of deals) {
    if (!isOpenDeal(d) || !d.assessment) continue
    assessed += 1
    const a = d.assessment
    const recent = checkOf(d, 'recentActivity'), close = checkOf(d, 'closeDate'), progress = checkOf(d, 'stageProgress')
    const share = sharePct(d.basePence, totalPipelinePence)
    const stage = stageDisplay(d.stage)
    const candidates: { rule: 1 | 2 | 3 | 4; reason: string; action: AttentionAction; families: readonly string[] }[] = []
    if (a.stage.over && recent?.reason === 'never_logged') {
      const days = a.stage.days ?? 0
      let reason = `${days} ${plural(days, 'day', 'days')} in ${stage} with nothing logged.`
      if (d.expectedClose && progress?.count != null && progress.count > 0 && progress.days != null) {
        reason += ` Closes ${dayMonthIso(d.expectedClose)}, so it has ${progress.count} ${plural(progress.count, 'stage', 'stages')} to go in ${progress.days} ${plural(progress.days, 'day', 'days')}.`
      } else if (d.expectedClose) {
        reason += ` Closes ${dayMonthIso(d.expectedClose)}.`
      }
      candidates.push({ rule: 1, reason, action: 'Open deal', families: ['stage_age', 'recent_activity'] })
    }
    if (share != null && share > CONCENTRATION_PCT && recent?.reason === 'quiet') {
      const days = recent.days ?? 0
      candidates.push({ rule: 2, reason: `${share}% of your open pipeline. Nothing has been logged for ${days} ${plural(days, 'day', 'days')}.`, action: 'Open deal', families: ['recent_activity'] })
    }
    if (close?.reason === 'no_close_date') {
      candidates.push({ rule: 3, reason: "No close date, so it's left out of the forecast and the timeline.", action: 'Open deal', families: [] })
    } else if (close?.reason === 'close_passed' && d.expectedClose) {
      candidates.push({ rule: 4, reason: `Close date passed on ${dayMonthIso(d.expectedClose)}.`, action: 'Open deal', families: ['close_date'] })
    }
    if (candidates.length === 0) continue
    const live = candidates.find(c => !dismissedFor(d, c.families))
    const pick = live ?? candidates[0]
    const row: AttentionRow = { deal: d, rule: pick.rule, reason: pick.reason, action: pick.action, weight: (d.basePence ?? 0) * SEVERITY[a.status] }
    if (live) flagged.push(row); else held.push(row)
  }
  const order = (x: AttentionRow, y: AttentionRow) => y.weight - x.weight || x.deal.name.localeCompare(y.deal.name)
  flagged.sort(order); held.sort(order)
  return { rows: flagged.slice(0, max), count: flagged.length, pence: flagged.reduce((s, r) => s + (r.deal.basePence ?? 0), 0), assessed, dismissed: held }
}

// ── Up next — future items only: meetings, dated tasks, deal close dates ─────
export type UpNextItem =
  | { kind: 'activity'; at: string; activity: SalesActivity; title: string; sub: string; amber: string | null }
  | { kind: 'close'; at: string; deal: SalesDeal; title: string; sub: string; amber: null }

/** A typed meeting on a weekend or an England & Wales bank holiday (the
 *  workingDays table, London day): "Sun 18 Oct is a Sunday" / "Mon 25 May is a
 *  bank holiday". Null on a working day — and null for an uncovered year's
 *  holidays, which the table cannot see (its weekend half still applies). */
export function offDayLine(dueAt: string): string | null {
  const day = dueDayOf(dueAt)
  if (!day) return null
  const d = new Date(`${day}T12:00:00`)
  const label = `${WEEKDAY_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`
  if (isBankHoliday(day)) return `${label} is a bank holiday`
  const wd = d.getDay()
  if (wd === 0 || wd === 6) return `${label} is a ${WEEKDAY[wd]}`
  return null
}

function closeSub(d: SalesDeal, todayIso: string): string {
  const close = ymd(d.expectedClose ?? '')
  const days = Math.round((Date.parse(`${close}T00:00:00Z`) - Date.parse(`${todayIso}T00:00:00Z`)) / DAY_MS)
  const progress = checkOf(d, 'stageProgress'), recent = checkOf(d, 'recentActivity')
  const when = days === 0 ? 'today' : `in ${days} ${plural(days, 'day', 'days')}`
  const head = progress?.count != null && progress.count > 0
    ? `${progress.count} ${plural(progress.count, 'stage', 'stages')} to go ${when}`
    : `closes ${when}`
  return `${stageDisplay(d.stage)} · ${head}${recent?.reason === 'never_logged' ? ' · nothing logged yet' : ''}`
}

/** The card's rows: PENDING activities due from now on and OPEN deals whose
 *  close date is today or later, merged by date, the first `max`. A logged
 *  (done) or overdue activity is never up next — Activities keeps those. */
export function upNextItems(activities: SalesActivity[], deals: SalesDeal[], now: Date, fmt: (pence: number, currency?: string) => string, max = UP_NEXT_ROWS): UpNextItem[] {
  const nowMs = now.getTime(), today = localIsoDate(now)
  const items: UpNextItem[] = []
  for (const a of activities) {
    if (!isPendingActivity(a) || !a.dueAt || Date.parse(a.dueAt) < nowMs) continue
    items.push({ kind: 'activity', at: a.dueAt, activity: a, title: a.subject, sub: upNextWhen(a, nowMs), amber: a.type === 'meeting' ? offDayLine(a.dueAt) : null })
  }
  for (const d of deals) {
    if (!isOpenDeal(d) || !d.expectedClose || ymd(d.expectedClose) < today) continue
    items.push({ kind: 'close', at: `${ymd(d.expectedClose)}T12:00:00`, deal: d, title: `${d.name} closes · ${fmt(d.amount, d.currency)}`, sub: closeSub(d, today), amber: null })
  }
  items.sort((x, y) => Date.parse(x.at) - Date.parse(y.at) || x.title.localeCompare(y.title))
  return items.slice(0, max)
}

// ── Inbound — the queue's size, its oldest lead, the account_id match ─────────
export interface InboundCard { count: number; oldest: SalesLead; waitedDays: number; matched: number; title: string; body: string }

/** "{m} of the {n} already match an open deal, so most can be attached" ONLY
 *  when m > n/2 (R-5); otherwise the plain count; none → says none. The match
 *  is the ruled one: the lead's account_id is an OPEN deal's account_id. */
export function inboundCard(leads: SalesLead[], deals: SalesDeal[], now: Date): InboundCard | null {
  const n = leads.length
  if (n === 0) return null
  const oldest = leads.reduce((a, b) => (Date.parse(b.createdAt) < Date.parse(a.createdAt) ? b : a))
  const waitedDays = Math.max(0, Math.floor((now.getTime() - Date.parse(oldest.createdAt)) / DAY_MS))
  const openAccounts = new Set(deals.filter(d => isOpenDeal(d) && d.accountId).map(d => d.accountId as string))
  const matched = leads.filter(l => l.accountId && openAccounts.has(l.accountId)).length
  const title = `${n} new ${plural(n, 'lead is', 'leads are')} waiting`
  const who = `The oldest, ${oldest.name}${oldest.company || oldest.accountName ? ` (${oldest.company || oldest.accountName})` : ''}, has waited ${waitedDays} ${plural(waitedDays, 'day', 'days')}.`
  const match = matched === 0
    ? `None of the ${n} ${plural(n, 'matches', 'match')} an open deal yet.`
    : matched > n / 2
      ? `${matched} of the ${n} already match an open deal, so most can be attached instead of converted.`
      : `${matched} of ${n} already ${plural(matched, 'matches', 'match')} an open deal and can be attached.`
  return { count: n, oldest, waitedDays, matched, title, body: `${who} ${match}` }
}

// ── Home tabs H-3b (RULED 2026-10-18): Move to the next working day; the exclude toggle ──
const LONDON_HMS = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })

/** The London wall-clock time of an instant, as {h, m, s}. */
function londonTime(t: Date): { h: number; m: number; s: number } {
  const p = Object.fromEntries(LONDON_HMS.formatToParts(t).map(x => [x.type, x.value]))
  return { h: Number(p.hour), m: Number(p.minute), s: Number(p.second) }
}

/** The instant that reads as the given London wall-clock time on the given
 *  London day — found by taking the UTC guess and correcting it by London's
 *  offset at that guess (one more pass covers a guess that lands on the
 *  other side of a clock change). */
export function londonInstant(day: string, h: number, m: number, s: number): string {
  let guess = Date.parse(`${day}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}Z`)
  for (let i = 0; i < 2; i++) {
    const lt = londonTime(new Date(guess))
    const diff = ((lt.h * 3600 + lt.m * 60 + lt.s) - (h * 3600 + m * 60 + s)) * 1000
    if (diff === 0) break
    guess -= diff
  }
  return new Date(guess).toISOString()
}

export interface MoveTarget { day: string; dueAt: string; label: string }

/** Where "Move to …" sends a typed meeting that sits on a weekend or bank
 *  holiday: the NEXT working day after its London day, at the SAME London
 *  time of day. Null when the due day is already a working day (the button
 *  never renders) or the value is not a date. */
export function moveTarget(dueAt: string): MoveTarget | null {
  const day = dueDayOf(dueAt)
  if (!day || offDayLine(dueAt) == null) return null
  const next = addWorkingDaysIso(day, 1)
  const { h, m, s } = londonTime(new Date(dueAt))
  const d = new Date(`${next}T12:00:00`)
  return { day: next, dueAt: londonInstant(next, h, m, s), label: `${WEEKDAY_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]}` }
}

/** The Win-rate card's test-deal note with the toggle's action (RULED): off
 *  and matches exist → "Includes test deals (…). Exclude ›"; on → "Test deals
 *  hidden (n). Show ›". Null when nothing matches and the toggle is off. */
export function excludeNote(t: { count: number; titles: string[] } | null | undefined, on: boolean): { text: string; action: string } | null {
  if (on) return { text: `Test deals hidden (${t?.count ?? 0}).`, action: 'Show ›' }
  const base = testDealsNote(t)
  return base ? { text: base, action: 'Exclude ›' } : null
}

// ── Home tabs H-3b-1 (the H-3b recon rulings, Klemen 2026-10-19): the shared header and the tab switch ──

/** The switch's tabs. Team (H-4) is a value whether or not it shows: the
 *  page falls back to Overview when Team is not visible (teamView.teamVisible). */
export type HomeTab = 'overview' | 'my-day' | 'team'
export const HOME_TABS: readonly HomeTab[] = ['overview', 'my-day', 'team']
export const isHomeTab = (v: unknown): v is HomeTab => typeof v === 'string' && (HOME_TABS as readonly string[]).includes(v)

/** `?tab=` as the address carries it — null when absent or not a tab Home has. */
export function tabFromSearch(search: string): HomeTab | null {
  const v = new URLSearchParams(search).get('tab')
  return isHomeTab(v) ? v : null
}

/** The search string with `?tab=` set to the tab (Overview is the bare address,
 *  spec §2: `/sales`, `/sales?tab=my-day`, `/sales?tab=team`), or removed when tab is null.
 *  Every other parameter is kept as it was. */
export function searchWithTab(search: string, tab: HomeTab | null): string {
  const p = new URLSearchParams(search)
  if (tab && tab !== 'overview') p.set('tab', tab)
  else p.delete('tab')
  const s = p.toString()
  return s ? `?${s}` : ''
}

/** The greeting by the LONDON time of day (spec §2: "afternoon" from 12:00,
 *  "evening" from 18:00) — the server's day, never the browser's zone. */
export function greeting(now: Date): string {
  const { h } = londonTime(now)
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
}

/** The first visit of the London day opens My day when its list has open
 *  items (spec §2; H-3b-2: the list's open count, myDayView); every other
 *  visit opens the remembered tab. lastVisitDay is the
 *  London day of the previous visit, null when none is remembered. */
export function openingTab(remembered: HomeTab | null, lastVisitDay: string | null, today: string, due: number): HomeTab {
  if (lastVisitDay !== today && due > 0) return 'my-day'
  return remembered ?? 'overview'
}
